import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Toast } from 'primereact/toast';
import { ArrowLeft, ArrowRight, ChevronDown, ChevronUp, Download, NotebookPen, Presentation, X } from 'lucide-react';
import { fileExtension, TlrFile } from '../../businessLogicService/pickTeacherLedFiles';
import { downloadFile } from '../../businessLogicService/downloadFile';
import { PptxPreviewViewer } from './viewers';

interface Props {
  url: string;
  guide?: TlrFile | null;
  skillCode?: string;
  title?: string;
  onClose: () => void;
}

const focusRingClass =
  'focus:!tw-outline-none focus:!tw-shadow-none focus-visible:!tw-shadow-[inset_0_0_0_6px_#C2C9FE] focus-visible:!tw-border-4 focus-visible:!tw-border-solid focus-visible:!tw-border-indigo-600 focus-visible:!tw-bg-stone-700';

const navButtonClass = [
  '!tw-w-11 !tw-h-11 !tw-p-0 !tw-rounded-full !tw-border-2 !tw-border-solid !tw-bg-stone-600 !tw-border-stone-500 tw-text-white',
  'hover:!tw-bg-stone-700',
  focusRingClass,
  'disabled:!tw-bg-stone-800 disabled:!tw-border-stone-800 disabled:!tw-text-stone-500 disabled:!tw-opacity-100',
].join(' ');

const SlideViewer: FC<Props> = ({ url, guide, skillCode, title = 'Teacher-Led Lesson', onClose }) => {
  const [current, setCurrent] = useState(0);
  const [total, setTotal] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [downloadingKey, setDownloadingKey] = useState<string | null>(null);
  const op = useRef<OverlayPanel>(null);
  const toast = useRef<Toast>(null);
  const downloadsBtnRef = useRef<HTMLButtonElement>(null);
  const firstItemRef = useRef<HTMLButtonElement>(null);

  const code = skillCode ?? 'lesson';
  const resources = [
    {
      key: 'slides',
      Icon: Presentation,
      tone: 'purple',
      title: 'Class-ready resource',
      description: 'These slides as an editable file to modify or share.',
      url,
      filename: `${code}_slides.${fileExtension(url, 'pptx')}`,
    },
    ...(guide
      ? [
          {
            key: 'guide',
            Icon: NotebookPen,
            tone: 'sky',
            title: 'Teacher Guide',
            description: 'A planning resource to help you prepare your lesson',
            url: guide.url,
            filename: `${code}_teacher_guide.${fileExtension(guide.url, 'docx')}`,
          },
        ]
      : []),
  ];

  const handleDownload = useCallback(async (resource: (typeof resources)[number]) => {
    setDownloadingKey(resource.key);
    try {
      await downloadFile(resource.url, resource.filename);
    } catch {
      toast.current?.show({ severity: 'error', summary: 'Could not download this resource. Please try again.' });
    } finally {
      setDownloadingKey(null);
    }
  }, []);

  const navigate = useCallback(
    (delta: number) => {
      setCurrent(prev => {
        const next = prev + delta;
        if (next < 0 || total === null || next >= total) return prev;
        return next;
      });
    },
    [total],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (menuOpen) return;
      if (e.key === ' ' && (e.target as HTMLElement).closest('button')) return;
      if (e.key === 'ArrowRight' || e.key === ' ') navigate(1);
      if (e.key === 'ArrowLeft') navigate(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate, menuOpen]);

  const onReady = useCallback((slideCount?: number) => {
    if (slideCount) setTotal(slideCount);
  }, []);

  const onError = useCallback((message: string) => setError(message), []);

  return (
    <Dialog
      visible
      onHide={onClose}
      showHeader={false}
      className="!tw-w-screen !tw-h-screen !tw-max-w-none !tw-max-h-none !tw-m-0 !tw-p-0 !tw-rounded-none !tw-bg-stone-950"
      contentClassName="!tw-p-0 !tw-bg-stone-950 tw-bg-[radial-gradient(ellipse_at_50%_63%,_#12110E_0%,_#1D1B18_70%)] tw-h-full tw-flex tw-flex-col"
      maskClassName="!tw-bg-black"
    >
      <div className="tw-flex tw-items-center tw-gap-6 tw-px-20 tw-py-8">
        <div className="tw-flex-1 tw-font-sora tw-font-bold tw-text-xl tw-leading-7 tw-text-stone-50">{title}</div>
        <button
          ref={downloadsBtnRef}
          type="button"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onClick={e => op.current?.toggle(e)}
          className="tw-inline-flex tw-cursor-pointer tw-items-center tw-gap-3 tw-rounded-full tw-border-none tw-bg-indigo-700 tw-px-6 tw-py-3 tw-font-atkinson tw-text-base tw-font-bold tw-text-white focus:tw-outline-none focus-visible:tw-shadow-[inset_0_0_0_6px_#C2C9FE]"
        >
          <Download size={20} />
          Downloads
          {menuOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </button>
        <Button
          text
          rounded
          aria-label="Close"
          onClick={onClose}
          icon={<X size={24} />}
          className={`!tw-w-[58px] !tw-h-[58px] !tw-p-0 !tw-bg-transparent !tw-text-white hover:!tw-bg-stone-800 ${focusRingClass}`}
        />
      </div>

      <OverlayPanel
        ref={op}
        onShow={() => {
          setMenuOpen(true);
          firstItemRef.current?.focus();
        }}
        onHide={() => {
          setMenuOpen(false);
          downloadsBtnRef.current?.focus();
        }}
        className="!tw-w-[320px] !tw-rounded-2xl !tw-border-2 !tw-border-neutral-200 !tw-shadow-[0px_0px_15px_rgba(132,123,111,0.15)] before:!tw-hidden after:!tw-hidden"
        pt={{ content: { className: 'p-0' } }}
      >
        <div className="tw-flex tw-flex-col tw-gap-4 tw-px-5 tw-pb-5 tw-pt-3">
          <span className="tw-font-sora tw-text-sm tw-font-bold tw-text-neutral-700">Resources available</span>
          {resources.map((resource, index) => (
            <button
              key={resource.key}
              ref={index === 0 ? firstItemRef : undefined}
              type="button"
              onClick={() => handleDownload(resource)}
              disabled={downloadingKey === resource.key}
              className="tw-flex tw-w-full tw-cursor-pointer tw-items-start tw-gap-3 tw-rounded-xl tw-border-2 tw-border-b-4 tw-border-solid tw-border-neutral-200 tw-bg-neutral-50 tw-p-3 tw-text-left tw-transition-colors hover:tw-bg-neutral-100 hover:!tw-border-indigo-200 focus-visible:!tw-border-indigo-600 active:!tw-border-indigo-600"
            >
              <span
                className={`tw-flex tw-items-center tw-justify-center tw-rounded-md tw-p-2 ${
                  resource.tone === 'purple' ? 'tw-bg-purple-100' : 'tw-bg-sky-100'
                }`}
              >
                <resource.Icon
                  size={16}
                  className={resource.tone === 'purple' ? 'tw-text-purple-700' : 'tw-text-sky-700'}
                />
              </span>
              <span className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-1 tw-pr-2">
                <span className="tw-font-atkinson tw-text-base tw-font-bold tw-leading-5 tw-text-neutral-950 tw-whitespace-nowrap">
                  {resource.title}
                </span>
                <span className="tw-font-atkinson tw-text-xs tw-leading-4 tw-text-neutral-950">
                  {resource.description}
                </span>
              </span>
              {downloadingKey === resource.key ? (
                <i className="pi pi-spinner pi-spin tw-text-neutral-950" />
              ) : (
                <Download size={20} className="tw-shrink-0 tw-text-neutral-950" />
              )}
            </button>
          ))}
        </div>
      </OverlayPanel>

      <div className="tw-flex-1 tw-min-h-0 tw-px-32 tw-py-10">
        {error ? (
          <div className="tw-h-full tw-flex tw-items-center tw-justify-center tw-text-red-300 tw-text-sm">
            Viewer error: {error}
          </div>
        ) : (
          <PptxPreviewViewer url={url} current={current} onReady={onReady} onError={onError} />
        )}
      </div>

      <div className="tw-flex tw-items-center tw-justify-center tw-gap-6 tw-pb-10">
        <Button
          aria-label="Previous slide"
          disabled={current === 0}
          onClick={() => navigate(-1)}
          icon={<ArrowLeft size={20} />}
          className={navButtonClass}
        />
        <span className="tw-font-atkinson tw-font-bold tw-text-base tw-leading-5 tw-text-stone-100 tw-tabular-nums tw-min-w-[3.5rem] tw-text-center">
          {current + 1} of {total ?? '…'}
        </span>
        <Button
          aria-label="Next slide"
          disabled={total === null || current + 1 >= total}
          onClick={() => navigate(1)}
          icon={<ArrowRight size={20} />}
          className={navButtonClass}
        />
      </div>

      <Toast ref={toast} position="top-right" appendTo={document.body} />
    </Dialog>
  );
};

export default SlideViewer;
